import {
  ensureWindowsAPIs,
  WindowSegment,
} from "../BoundingWindowRectsPolyfill";
import type { LayoutBreakpoint, WindowMode } from "./layoutBreakpointTypings";

export interface SpanningChangedCallback {
  (isSpanning: boolean, windowMode: WindowMode): void;
}
const spanningHandlers: { [identifier: string]: SpanningChangedCallback } = {};

export interface WindowChangedCallback {
  (rects: WindowSegment[], windowMode: WindowMode): void;
}

const windowChangedHandlers: { [identifier: string]: WindowChangedCallback } =
  {};

export interface layoutBreakpointChangedCallback {
  (layoutBreakpoint: LayoutBreakpoint): void;
}

const layoutBreakpointChangedHandlers: {
  [identifier: string]: layoutBreakpointChangedCallback;
} = {};

export interface Point {
  x: number;
  y: number;
}

// Up to date list of the current windowRects
let windowRects: WindowSegment[] = [];

let keyboardRect: WindowSegment | undefined = undefined;

// The index of the occluded window rect, -1 if the keyboard isn't occluding
let occludedWindowRectIndex: number = -1;

// The current mode of the browser
let currentMode: WindowMode = "Undefined";

// The current display layoutBreakpoint
let currentLayoutBreakpoint: LayoutBreakpoint = "Default";

let initialized: boolean = false;

/**
 * Returns if we want to disable animations. It could be for performance
 * reasons or because of a user setting
 */
export function isReducedAnimations(): boolean {
  return window?.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Returns if we are currently in DP Mode.
 */
export function isDPMode(): boolean {
  return getWindowMode() === "DP";
}

/**
 * Returns if we are currently in DL Mode.
 */
export function isDLMode(): boolean {
  return getWindowMode() === "DL";
}

/**
 * Returns if we are currently in DP Mode.
 * This should only be used internally when setting the CurrentMode
 * and will return a value independent of CurrentMode
 */
function isDPModeInternal(): boolean {
  if (windowRects.length <= 1) {
    return false;
  }

  return windowRects[0].top === windowRects[1].top;
}

/**
 * Returns if we are currently in DL Mode.
 * This should only be used internally when setting the CurrentMode
 * and will return a value independent of CurrentMode
 */
function isDLModeInternal(): boolean {
  if (windowRects.length <= 1) {
    return false;
  }

  return windowRects[0].left === windowRects[1].left;
}

/**
 * Returns the WindowSegment that the element is within. If the element spans
 * multiple WindowSegments, it will only return the first one.
 *
 * Content can be any since the type can be React.RefObject<Element> specifically when this
 * is being called from Fabric. We don't want to pull in any React dependency
 * here to keep this code as lightweight as possible, hence the any usage
 */
export function getBoundingWindow(
  content?:
    | Element
    | string
    | MouseEvent
    | Point
    | null
    | any /* React.RefObject<Element> */
): WindowSegment | undefined {
  return getBoundingWindowWithPadding(content, 0 /*minPadding*/);
}

/**
 * Returns the WindowSegment that the element is within, with padding. If the element spans
 * multiple WindowSegments, it will only return the first one.
 *
 * Content can be any since the type can be React.RefObject<Element>, specifically when this
 * is being called from Fabric. We don't want to pull in any React dependency
 * here to keep this code as lightweight as possible, hence the any usage
 */
export function getBoundingWindowWithPadding(
  content:
    | Element
    | string
    | MouseEvent
    | Point
    | null
    | undefined
    | any /* React.RefObject<Element> */,
  minPadding: number
): WindowSegment | undefined {
  if (!content || !windowRects || windowRects.length <= 0) {
    return undefined;
  }

  // if we only have one windowRect we know which
  // window rect contains the content
  if (windowRects.length === 1) {
    return addPaddingToRect(windowRects[0], minPadding);
  }

  let contentRect: DOMRect | ClientRect | WindowSegment;

  if (typeof content === "string") {
    const contentElem = document.querySelector(content);

    if (contentElem) {
      contentRect = contentElem.getBoundingClientRect();
    } else {
      return undefined;
    }
  } else if ((content as MouseEvent).preventDefault !== undefined) {
    const ev = content as MouseEvent;
    contentRect = new WindowSegment(
      ev.clientX,
      ev.clientY,
      1 /*width */,
      1 /* height */
    );
  } else if ((content as Element).getBoundingClientRect) {
    contentRect = (content as Element).getBoundingClientRect();
  } else if (content.current) {
    contentRect = content.current.getBoundingClientRect();
  } else if (content as Point) {
    const point: Point = content as Point;
    contentRect = new WindowSegment(
      point.x,
      point.y,
      1 /*width */,
      1 /* height */
    );
  } else {
    // This case is only here to make lint happy. We can't actually arrive here.
    return undefined;
  }

  const windowRect = getBoundingRect(contentRect);

  return windowRect && addPaddingToRect(windowRect, minPadding);
}

/**
 * Returns the WindowSegment that the element is within, with padding. If the element spans
 * multiple WindowSegments, it will only return the first one.
 * This is needed to fulfill the signature for the fabric callback.
 *
 * Content can be any since the type can be React.RefObject<Element> specifically when this
 * is being called from Fabric. We don't want to pull in any React dependency
 * here to keep this code as lightweight as possible, hence the any usage
 */
export function getBoundingWindowWithPaddingFabric(
  content?:
    | Element
    | string
    | MouseEvent
    | Point
    | null
    | any /* React.RefObject<Element> */,
  _?: Window
): WindowSegment | undefined {
  return getBoundingWindowWithPadding(content, 8 /* minPadding */);
}

function addPaddingToRect(rect: WindowSegment, padding: number) {
  return (
    rect &&
    // We must use ES5 syntax to keep the bundle as lightweight as possible
    // so the js polyfills don't get pulled in from ts
    (Object as any).assign({}, rect, {
      top: rect.top + padding,
      left: rect.left + padding,
      right: rect.right - padding,
      bottom: rect.bottom - padding,
      width: rect.width - padding * 2,
      height: rect.height - padding * 2,
    })
  );
}

/**
 * Returns the WindowSegment that the element is within. If the element spans
 * multiple WindowSegments, it will only return the first one.
 */
export function getBoundingRect(
  elementRect: DOMRect | ClientRect | WindowSegment
): WindowSegment | undefined {
  if (isRectSpanning(elementRect)) {
    return windowRects[0];
  }

  // We must use ES5 syntax to keep the bundle as lightweight as possible
  // so the js polyfills don't get pulled in from ts
  return (windowRects as any).find((windowRect: WindowSegment) => {
    return isRectWithin(elementRect, windowRect);
  });
}

/**
 * Returns if the given elementRect is fully contained in the passed in WindowSegment
 */
export function isRectWithin(
  elementRect: DOMRect | ClientRect,
  windowRect: WindowSegment
): boolean {
  return (
    elementRect.left >= windowRect.left &&
    elementRect.top >= windowRect.top &&
    elementRect.right <= windowRect.left + windowRect.width &&
    elementRect.bottom <= windowRect.top + windowRect.height
  );
}

/**
 * Returns if the element is spanning multiple windowRects
 */
export function isElementSpanning(element: HTMLElement): boolean {
  if (
    element === undefined ||
    (getWindowMode() !== "DL" && getWindowMode() !== "DP")
  ) {
    return false;
  }

  return isRectSpanning(element.getBoundingClientRect());
}

/**
 * Returns if the passed in Rect is spanning multiple windowRects
 */
export function isRectSpanning(elementRect: DOMRect | ClientRect): boolean {
  if (windowRects.length <= 1) {
    return false;
  }

  // if the element right bounds are larger than the first windowRects right bounds
  if (elementRect.right > windowRects[0].left + windowRects[0].width) {
    // and the element left bounds are smaller than the second windowRects left bounds
    return elementRect.left < windowRects[1].left;
  }
  // if the element bottom bounds are larger than the first windowRects bottom bounds
  if (elementRect.bottom > windowRects[0].top + windowRects[0].height) {
    // and the element top bounds are smaller than the second windowRects top bounds
    return elementRect.top < windowRects[1].top;
  }

  return false;
}

/**
 * Returns how many pixels the Rect is spanning multiple windowRects
 */
export function getRectSpanningOffset(
  elementRect: DOMRect | ClientRect,
  isRtl?: boolean
): number {
  if (windowRects.length <= 1) {
    return -1;
  }

  if (isRtl) {
    if (elementRect.left < windowRects[1].left) {
      return elementRect.right - windowRects[0].right;
    }

    if (elementRect.top < windowRects[1].top) {
      return elementRect.bottom - windowRects[0].bottom;
    }
  } else {
    if (elementRect.right > windowRects[0].left + windowRects[0].width) {
      return windowRects[1].left - elementRect.left;
    }

    if (elementRect.bottom > windowRects[0].top + windowRects[0].height) {
      return windowRects[1].top - elementRect.top;
    }
  }

  return -1;
}

/**
 * Notifies any listeners that the spanning state has changed.
 */
function fireSpanningChangedEvent(isSpanning: boolean) {
  for (const index in spanningHandlers) {
    spanningHandlers[index](isSpanning, currentMode);
  }
}

/**
 * Registers a callback to be called when the device changes it’s spanning state.
 * This will fire when we change our spanning state or the orientation changes. The
 * callback will have a boolean parameter with the spanning state.
 */
export function registerSpanningChanged(
  identifier: string,
  callback: SpanningChangedCallback
): void {
  spanningHandlers[identifier] = callback;
}

export function unregisterSpanningChanged(identifier: string): void {
  if (spanningHandlers[identifier] !== undefined) {
    delete spanningHandlers[identifier];
  }
}

/**
 * Notifies any listeners that the window rects have changed.
 */
function fireWindowChangedEvent() {
  // We must use ES5 syntax to keep the bundle as lightweight as possible
  // so the js polyfills don't get pulled in from ts
  const allWindowRects = windowRects;

  if (keyboardRect) {
    allWindowRects.push(keyboardRect);
  }

  for (const index in windowChangedHandlers) {
    windowChangedHandlers[index](allWindowRects, currentMode);
  }
}

/**
 * Registers a callback to be called when the bounding rects of the app changes.
 */
export function registerBoundingWindowRectsChanged(
  identifier: string,
  callback: WindowChangedCallback
): void {
  windowChangedHandlers[identifier] = callback;
}

export function unregisterBoundingWindowRectsChanged(identifier: string): void {
  if (windowChangedHandlers[identifier] !== undefined) {
    delete windowChangedHandlers[identifier];
  }
}

export function registerLayoutBreakpointChanged(
  identifier: string,
  callback: layoutBreakpointChangedCallback
): void {
  layoutBreakpointChangedHandlers[identifier] = callback;
}

export function unregisterLayoutBreakpointChanged(identifier: string): void {
  if (layoutBreakpointChangedHandlers[identifier] !== undefined) {
    delete layoutBreakpointChangedHandlers[identifier];
  }
}

/**
 * Notifies any listeners that the display layoutBreakpoint has changed.
 */
function fireLayoutBreakpointChangedEvent() {
  for (const index in layoutBreakpointChangedHandlers) {
    layoutBreakpointChangedHandlers[index](currentLayoutBreakpoint);
  }
}

/**
 * This gets called when the onBoundingWindowRectsChange event fires. It will
 * update the local values that we expose to clients and will fire any necessary
 * callbacks
 */
export function onWindowRectsChange(): void {
  // If this function gets called, make sure all of the apis we expect
  // are available (potentially polyfilled)
  if (!window.getWindowSegments) {
    ensureWindowsAPIs();
  }
  const newRects: WindowSegment[] = window.getWindowSegments!();

  let spanningChanged = false;
  if (newRects.length !== windowRects.length) {
    spanningChanged = true;
  }

  windowRects = newRects;

  if (windowRects.length === 1) {
    const rect = windowRects[0];
    currentMode = rect.width > rect.height ? "Landscape" : "Portrait";
  } else if (isDPModeInternal()) {
    spanningChanged = spanningChanged || currentMode === "DL";
    currentMode = "DP";
  } else if (isDLModeInternal()) {
    spanningChanged = spanningChanged || currentMode === "DP";
    currentMode = "DL";
  }

  const originalLayoutBreakpoint = currentLayoutBreakpoint;

  // Update the current layoutBreakpoint always based off of the first rect
  if (currentMode === "Landscape" || currentMode === "DL") {
    currentLayoutBreakpoint = windowRects[0].width <= 960 ? "Small" : "Large";
  } else if (currentMode === "Portrait" || currentMode === "DP") {
    currentLayoutBreakpoint = windowRects[0].width <= 720 ? "Small" : "Large";
  }

  fireWindowChangedEvent();

  if (spanningChanged) {
    fireSpanningChangedEvent(currentMode === "DL" || currentMode === "DP");
  }

  if (originalLayoutBreakpoint !== currentLayoutBreakpoint) {
    fireLayoutBreakpointChangedEvent();
  }
}

export function getWindowMode(): WindowMode {
  // If we haven't stored the currentMode (e.g. spanning hasn't changed)
  // calculate it from scratch until we have stored it
  if (currentMode === "Undefined") {
    const windowSegments: WindowSegment[] | undefined = window.getWindowSegments
      ? window.getWindowSegments()
      : undefined;

    // If we either don't get windowSegments or we only have one,
    // return landscape or portrait
    if (!windowSegments || windowSegments.length <= 1) {
      return window.innerWidth > window.innerHeight ? "Landscape" : "Portrait";
    }

    // If we have more than one windowSegment (two, is currently the only other option)
    // we know if we're landscape then we are DP, otherwise we're DL
    return window.innerWidth > window.innerHeight ? "DP" : "DL";
  }
  return currentMode;
}

export function getLayoutBreakpoint(): LayoutBreakpoint {
  return currentLayoutBreakpoint;
}

export function getKeyboardSegment(): WindowSegment | undefined {
  return keyboardRect;
}

export function getWindowRectsWithoutKeyboard(): WindowSegment[] {
  return windowRects;
}

/**
 * Call this to initialize the WindowUtil apis. When called, we will register an event
 * listener with the window for onBoundingWindowRectsChange
 */
export function initializeBaseWindowUtil(): void {
  if (initialized) {
    return;
  }

  enableVirtualKeyboardOverlaysContent();
  ensureWindowsAPIs();
  window.addEventListener("onBoundingWindowRectsChange", onWindowRectsChange);

  if (!windowRects || windowRects.length === 0) {
    // Initialize the window rects and fire any callbacks if they don't yet exist.
    onWindowRectsChange();
  }

  initialized = true;
}

// This will always be needed because it tells the browser
// to allow the virtual keyboard to overlay contents
function enableVirtualKeyboardOverlaysContent() {
  if (
    typeof navigator !== "undefined" &&
    (navigator as any).virtualKeyboard &&
    (navigator as any).virtualKeyboard.overlaysContent !== undefined
  ) {
    // This can only be set if we are the outermost window
    if (window === top) {
      (navigator as any).virtualKeyboard.overlaysContent = true;
    }

    (navigator as any).virtualKeyboard.addEventListener(
      "overlaygeometrychange",
      overlayGeometryChanged
    );
  }
}

function overlayGeometryChanged(event: any): void {
  const { width, height, left } = event.boundingRect;

  if (width === 0 || height === 0) {
    keyboardRect = undefined;
    occludedWindowRectIndex = -1;
  } else {
    keyboardRect = event.boundingRect;
    keyboardRect!.isOccluded = true;

    if (left > 0 || getWindowMode() === "DL") {
      occludedWindowRectIndex = 1;
    } else {
      occludedWindowRectIndex = 0;
    }
  }

  onWindowRectsChange();
}

export function getWindowRectForKeyboardSegment(): WindowSegment | undefined {
  if (!keyboardRect) {
    return undefined;
  }

  return windowRects[occludedWindowRectIndex];
}

initializeBaseWindowUtil();
